import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { 
  Mic, 
  MicOff, 
  Volume2, 
  Loader2,
  Command,
  CheckCircle,
  XCircle,
  HelpCircle,
  ArrowRight
} from 'lucide-react';

// Voice command definitions
const VOICE_COMMANDS = [
  { phrase: ['go to patients', 'open patients', 'show patients', 'children'], action: 'navigate', path: '/patients', description: 'Open Patients page' },
  { phrase: ['go to dashboard', 'open dashboard', 'home'], action: 'navigate', path: '/', description: 'Go to Dashboard' },
  { phrase: ['go to calendar', 'open calendar', 'appointments', 'schedule'], action: 'navigate', path: '/calendar', description: 'Open Calendar' },
  { phrase: ['go to staff', 'open staff', 'staff directory'], action: 'navigate', path: '/staff', description: 'Open Staff Directory' },
  { phrase: ['go to hr', 'open hr', 'human resources'], action: 'navigate', path: '/hr', description: 'Open HR page' },
  { phrase: ['go to finance', 'open finance', 'financial'], action: 'navigate', path: '/finance', description: 'Open Finance' },
  { phrase: ['go to payroll', 'open payroll'], action: 'navigate', path: '/payroll', description: 'Open Payroll' },
  { phrase: ['go to audit', 'open audit', 'audit logs'], action: 'navigate', path: '/audit', description: 'Open Audit Logs' },
  { phrase: ['go to crm', 'open crm', 'leads'], action: 'navigate', path: '/crm', description: 'Open CRM' },
  { phrase: ['go to messages', 'open messages', 'email'], action: 'navigate', path: '/messages', description: 'Open Messages' },
  { phrase: ['go to settings', 'open settings'], action: 'navigate', path: '/settings', description: 'Open Settings' },
  { phrase: ['go to analytics', 'open analytics'], action: 'navigate', path: '/analytics', description: 'Open Analytics' },
  { phrase: ['new assessment', 'create assessment', 'start assessment'], action: 'navigate', path: '/assessments/new', description: 'New Assessment' },
  { phrase: ['ai assistant', 'open ai', 'assistant'], action: 'navigate', path: '/ai-assistant', description: 'Open AI Assistant' },
  { phrase: ['ai workforce', 'open workforce'], action: 'navigate', path: '/ai-workforce', description: 'Open AI Workforce' },
  { phrase: ['help', 'commands', 'what can you do'], action: 'help', description: 'Show available commands' }
];

// Voice Command Button - Shows in dashboard for Super Admin
export function VoiceCommandButton() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [lastCommand, setLastCommand] = useState(null);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);

  // Check if user is Super Admin
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  // Initialize speech recognition
  useEffect(() => {
    if (!isSuperAdmin) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('Speech recognition not supported in this browser');
      return;
    }

    const recognitionInstance = new SpeechRecognition();
    recognitionInstance.continuous = false;
    recognitionInstance.interimResults = true;
    recognitionInstance.lang = 'en-GB';
    recognitionInstance.maxAlternatives = 3;

    recognitionInstance.onresult = (event) => {
      const current = event.resultIndex;
      const result = event.results[current];
      const text = result[0].transcript.toLowerCase().trim();
      setTranscript(text);

      if (result.isFinal) {
        processCommand(text);
      }
    };

    recognitionInstance.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') {
        toast.info('No speech detected. Try again.');
      } else if (event.error === 'not-allowed') {
        setError('Microphone access denied');
        toast.error('Please allow microphone access to use voice commands');
      } else {
        toast.error(`Speech recognition error: ${event.error}`);
      }
      setIsListening(false);
    };

    recognitionInstance.onend = () => {
      setIsListening(false);
    };

    setRecognition(recognitionInstance);

    return () => {
      if (recognitionInstance) {
        recognitionInstance.abort();
      }
    };
  }, [isSuperAdmin]);

  const processCommand = useCallback((text) => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Find matching command
    const matchedCommand = VOICE_COMMANDS.find(cmd => 
      cmd.phrase.some(phrase => text.includes(phrase))
    );

    if (matchedCommand) {
      setLastCommand({ text, matched: true, command: matchedCommand });
      
      if (matchedCommand.action === 'navigate') {
        toast.success(`Navigating to ${matchedCommand.description}`);
        navigate(matchedCommand.path);
      } else if (matchedCommand.action === 'help') {
        setShowHelp(true);
      }
    } else {
      setLastCommand({ text, matched: false });
      toast.info(`Command not recognized: "${text}". Say "help" for available commands.`);
    }

    // Clear transcript after processing
    timeoutRef.current = setTimeout(() => {
      setTranscript('');
      setLastCommand(null);
    }, 3000);
  }, [navigate]);

  const startListening = () => {
    if (!recognition) {
      toast.error('Speech recognition not available');
      return;
    }

    setTranscript('');
    setLastCommand(null);
    setIsListening(true);

    try {
      recognition.start();
      toast.info('Listening... Say a command');
    } catch (err) {
      if (err.name === 'InvalidStateError') {
        // Already listening
        recognition.abort();
        setTimeout(() => {
          recognition.start();
        }, 100);
      }
    }
  };

  const stopListening = () => {
    if (recognition) {
      recognition.stop();
    }
    setIsListening(false);
  };

  if (!isSuperAdmin) return null;

  if (error) {
    return (
      <Button
        variant="outline"
        className="rounded-full border-red-200 text-red-600"
        onClick={() => setShowHelp(true)}
        title={error}
      >
        <MicOff className="w-4 h-4 mr-2" />
        Voice Unavailable
      </Button>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant={isListening ? "default" : "outline"}
          className={`rounded-full ${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'border-purple-200 text-purple-600 hover:bg-purple-50'}`}
          onClick={isListening ? stopListening : startListening}
          data-testid="voice-command-btn"
        >
          {isListening ? (
            <>
              <Volume2 className="w-4 h-4 mr-2" />
              Listening...
            </>
          ) : (
            <>
              <Mic className="w-4 h-4 mr-2" />
              Voice Command
            </>
          )}
        </Button>
        
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          onClick={() => setShowHelp(true)}
          title="Voice command help"
        >
          <HelpCircle className="w-4 h-4 text-slate-400" />
        </Button>
      </div>

      {/* Live transcript display */}
      {(isListening || transcript) && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-4 min-w-[300px]">
            <div className="flex items-center gap-2 mb-2">
              {isListening && (
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
              )}
              <span className="text-sm font-medium text-slate-700">
                {isListening ? 'Listening...' : 'Processing...'}
              </span>
            </div>
            {transcript && (
              <p className="text-lg text-slate-900">"{transcript}"</p>
            )}
            {lastCommand && (
              <div className={`mt-2 flex items-center gap-2 ${lastCommand.matched ? 'text-green-600' : 'text-amber-600'}`}>
                {lastCommand.matched ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">{lastCommand.command.description}</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm">Command not recognized</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Help Dialog */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Command className="w-5 h-5 text-purple-600" />
              Voice Commands
            </DialogTitle>
            <DialogDescription>
              Available voice commands for hands-free navigation
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[400px] overflow-y-auto">
            <div className="space-y-2">
              {VOICE_COMMANDS.filter(cmd => cmd.action === 'navigate').map((cmd, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-slate-900">{cmd.description}</p>
                    <p className="text-xs text-slate-500">
                      Say: "{cmd.phrase[0]}"
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-4">
            <div className="flex items-start gap-2">
              <Mic className="w-4 h-4 text-purple-600 mt-0.5" />
              <div className="text-sm text-purple-800">
                <p className="font-medium">Tips</p>
                <ul className="text-purple-600 mt-1 space-y-1">
                  <li>• Speak clearly and naturally</li>
                  <li>• Click the microphone button to start</li>
                  <li>• Say "help" to see this list anytime</li>
                </ul>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Voice Command Widget for Dashboard
export function VoiceCommandWidget() {
  const { user } = useAuth();
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  if (!isSuperAdmin) return null;

  return (
    <div className="flex items-center gap-4">
      <VoiceCommandButton />
    </div>
  );
}

export default VoiceCommandButton;
